/**
 * Turn the box's self-report into sentences an operator can act on.
 *
 * The box sends facts, not conclusions: a handshake age, an internet verdict, a
 * restart count, and a frozen post-mortem line. On their own they mean nothing
 * to the person standing in the shop. The whole reason this feature exists is
 * that "Offline" told operators nothing, so they rebooted, and two of them
 * shipped vendos back from an island. Raw numbers on the same screen would be
 * the same failure in a different font.
 *
 * Kept separate from the component, and pure, so the wording is testable — this
 * text is what an operator will act on, and getting a cause backwards is worse
 * than showing nothing.
 */

/** Matches the box's own threshold for "the tunnel is down". */
const TUNNEL_STALE_SEC = 240;

export interface RawDiag {
   hsAge?: number;
   inet?: string;
   reboots24h?: number;
   outage?: string;
   events?: string[];
   /** The repair record the router sends. Dropped by the server until step 12,
    *  so older stored reports carry none. */
   actions?: string[];
   /** 'on' = the router drops another router's DHCP on its LAN; 'off' = this
    *  image cannot (it still keeps its own DHCP running); absent/'' = an agent
    *  that predates it. */
   dhcpGuard?: string;
   /** Other routers caught handing out addresses on the LAN in the last hour. */
   dhcpRogue?: { mac: string; port: string }[];
   at?: string | Date | null;
}

/** "LAN port 3" for lan3; a Wi-Fi interface means it joined over the Wi-Fi. */
function lanPortName(port: string): string {
   const m = /^lan(\d+)$/.exec(port);
   if (m) return `LAN port ${m[1]}`;
   if (/^phy\d|^wlan|-ap\d/.test(port)) return 'the Wi-Fi';
   return port || 'the LAN';
}

/**
 * The owner's note when another router on the LAN is handing out addresses —
 * usually an access point added with its DHCP left on (JOHN JOHN, 2026-10-26:
 * a Tenda on LAN 3 took the NodeMCU and every phone offline). Null when there
 * is nothing to say.
 */
export function rogueDhcpLine(diag?: RawDiag | null): string | null {
   const rogue = diag?.dhcpRogue ?? [];
   if (!rogue.length) return null;
   const where = [...new Set(rogue.map((r) => lanPortName(r.port)))].join(' and ');
   const one = rogue.length === 1;
   const what = one
      ? `A router or AP ${where.startsWith('LAN') ? 'plugged into' : 'on'} ${where} has its DHCP turned on.`
      : `${rogue.length} routers or APs on ${where} have their DHCP turned on.`;
   const them = one ? 'that router or AP' : 'those routers or APs';
   const it = one ? 'it' : 'them';
   return diag?.dhcpGuard === 'on'
      ? `${what} SmartFi is blocking ${it} so your customers stay online. To clear this message, turn off DHCP on ${them}, or set ${it} to AP mode.`
      : `${what} This can cut the NodeMCU and customers off the internet. Turn off DHCP on ${them}, set ${it} to AP mode, or unplug ${it}.`;
}

/** "5 hours 6 minutes", "3 minutes", "45 seconds" — never "0". */
export function humanDuration(totalSec: number): string {
   const s = Math.max(0, Math.trunc(totalSec));
   if (s < 60) return `${s} second${s === 1 ? '' : 's'}`;
   const m = Math.floor(s / 60);
   if (m < 60) return `${m} minute${m === 1 ? '' : 's'}`;
   const h = Math.floor(m / 60);
   const rm = m % 60;
   const hp = `${h} hour${h === 1 ? '' : 's'}`;
   return rm ? `${hp} ${rm} minute${rm === 1 ? '' : 's'}` : hp;
}

/**
 * Is this report CURRENT, or a snapshot from before the box went quiet?
 *
 * 🔴 Ray caught this on his own bench box, 2026-08-11: it had been silent for
 * 4.3 hours and the panel still read "Connected to SmartFi" — the last thing the
 * box managed to say, shown in the present tense. A screen built to stop people
 * guessing was itself lying by omission.
 *
 * Freshness comes from when the ROUTER last spoke to us, never from the numbers
 * inside the report: those describe the moment the report was made, so a stale
 * report always looks perfectly healthy to itself.
 */
const REPORT_FRESH_SEC = 240;

export function isReportStale(
   lastSeenAt: string | Date | null | undefined,
   now: Date = new Date()
): boolean {
   if (!lastSeenAt) return true;
   const t = new Date(lastSeenAt).getTime();
   if (!Number.isFinite(t)) return true;
   return now.getTime() - t > REPORT_FRESH_SEC * 1000;
}

/**
 * "reported just now" · "reported 2 min ago" · "reported 3 hrs ago"
 *
 * Ray, 2026-08-11: he opened this screen seconds after powering a box on, saw
 * nothing new, and had no way to tell whether the box had not spoken yet or the
 * screen was simply behind. He had to ask. A screen that does not say how old it
 * is makes people guess, and the guessing is what this whole feature exists to
 * end.
 *
 * Measured from when the box last SPOKE, not from when the report last changed.
 * Since the box now sends only on change, a perfectly healthy box can carry a
 * report whose content is fifteen minutes old — saying "reported 15 min ago"
 * there would read as a problem when there is none.
 */
export function reportedAgo(
   lastSeenAt: string | Date | null | undefined,
   now: Date = new Date()
): string {
   if (!lastSeenAt) return 'never reported';
   const t = new Date(lastSeenAt).getTime();
   if (!Number.isFinite(t)) return 'never reported';
   const sec = Math.round((now.getTime() - t) / 1000);
   if (sec < 90) return 'reported just now';
   if (sec < 3600) return `reported ${Math.round(sec / 60)} min ago`;
   const hrs = Math.round(sec / 3600);
   if (hrs < 48) return `reported ${hrs} hr${hrs === 1 ? '' : 's'} ago`;
   return `reported ${Math.round(hrs / 24)} days ago`;
}

export function tunnelLine(hsAge: number | undefined): string {
   // 🔴 -1 used to read "Never connected to SmartFi". It is not a measurement:
   // the server writes -1 whenever the router reported no handshake age, so
   // that sentence was invented about 79 live, healthy vendos. Unknown is
   // unknown — see tunnelState().
   if (tunnelState(hsAge) === 'unknown') return 'Not reported yet';
   if ((hsAge as number) <= TUNNEL_STALE_SEC) return 'Connected to SmartFi';
   return `Lost — last connected ${humanDuration(hsAge as number)} ago`;
}

export function internetLine(inet: string | undefined): string {
   if (inet === 'up') return 'Working';
   if (inet === 'down') return 'Not working';
   return 'Not reported yet';
}

/**
 * The line that settles the argument. The box freezes this at its first run
 * after a restart, because the alternative — asking a running box how long it
 * was down — cannot work: it keeps counting once it is back.
 *
 * Input looks like: `boot=1786326000 dark=18360 cause=watchdog reboot`
 */
export function outageLine(outage: string | undefined): string | null {
   if (!outage) return null;
   const dark = /dark=(\d+)/.exec(outage);
   if (!dark) return null;
   const who = /cause=(.*)$/.exec(outage)?.[1]?.trim() ?? '';
   const how = humanDuration(Number(dark[1]));
   // A REMOTE reboot is knowable, so name it. Anything we can name beats the
   // three-way guess — that guess exists only for the case the box genuinely
   // cannot distinguish.
   if (who.includes('remote')) {
      return `Was down for ${how} — you restarted it from the dashboard`;
   }
   // "watchdog reboot" is the box saying it saved itself. Anything else is the
   // box saying it did NOT — a power cut, a person, or a crash. It cannot tell
   // those three apart, and pretending otherwise would be a guess shown as fact.
   if (who.includes('watchdog')) {
      return `Was down for ${how}, then restarted itself and recovered`;
   }
   return `Was down for ${how} — power was cut, someone restarted it, or it crashed`;
}

export function restartLine(reboots24h: number | undefined): string | null {
   const n = reboots24h ?? 0;
   if (n <= 0) return null;
   return `Restarted itself ${n} time${n === 1 ? '' : 's'} in the last day`;
}

export interface DiagEvent {
   at: Date;
   text: string;
   /** Drives the colour. Not decoration — the one an operator must not miss is
    *  "we lost the box while its shop was fine", and it should not look like
    *  the line above it. */
   tone: 'ok' | 'bad' | 'warn';
}

/**
 * "just now (1:12 PM)" · "23 min ago (12:49 PM)" · "3 hrs ago (10:54 AM)" ·
 * "yesterday (3:33 AM)" · "Aug 9 (3:33 AM)"
 *
 * Both halves on purpose: the relative part is how someone thinks about it
 * ("that was this morning"), the clock time is what they match against a
 * customer saying "it died around ten".
 */
export function friendlyWhen(at: Date, now: Date = new Date()): string {
   const clock = at
      .toLocaleTimeString(undefined, {
         hour: 'numeric',
         minute: '2-digit',
      })
      .toUpperCase();
   const sec = Math.round((now.getTime() - at.getTime()) / 1000);

   if (sec < 90) return `just now (${clock})`;
   if (sec < 3600) return `${Math.round(sec / 60)} min ago (${clock})`;

   // Calendar days apart, NOT hours — a box that stopped at 11pm and is read at
   // 1am is "yesterday" to the person reading it, even though it is 2 hours.
   const d0 = new Date(now.getFullYear(), now.getMonth(), now.getDate());
   const d1 = new Date(at.getFullYear(), at.getMonth(), at.getDate());
   const days = Math.round((d0.getTime() - d1.getTime()) / 86_400_000);

   if (days === 0) {
      const hrs = Math.round(sec / 3600);
      return `${hrs} hr${hrs === 1 ? '' : 's'} ago (${clock})`;
   }
   if (days === 1) return `yesterday (${clock})`;
   const day = at.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
   });
   return `${day} (${clock})`;
}

/**
 * One recorded change of state. The box writes these lines:
 *   `<epoch> <up|down|unknown> <up|down|unknown> <up|down> boot=<epoch>`
 *          tunnel            internet          agent
 *
 * Anything that does not parse is skipped rather than shown half-read — this
 * file is written by a box that may lose power mid-write, which is exactly the
 * situation the whole feature exists to survive.
 */
export function parseEvent(line: string): DiagEvent | null {
   const p = line.trim().split(/\s+/);
   if (p.length < 4) return null;
   const epoch = Number(p[0]);
   if (!Number.isFinite(epoch) || epoch <= 0) return null;

   const [, tun, inet, agent] = p;
   let text: string;
   let tone: DiagEvent['tone'];
   if (tun === 'down' && inet === 'up') {
      // The case operators describe as "the internet is fine but SmartFi says
      // offline". Naming it explicitly is the point of this whole screen, and it
      // is the one line that must not look like the others.
      text = 'Lost SmartFi, but the shop internet was still working';
      tone = 'bad';
   } else if (tun === 'down' && inet === 'down') {
      text = 'Lost the shop internet, so it lost SmartFi too';
      tone = 'warn';
   } else if (tun === 'up') {
      text = 'Connected to SmartFi';
      tone = 'ok';
   } else {
      text = 'Connection state changed';
      tone = 'warn';
   }
   if (agent === 'down') {
      text += ' (the box software had stopped)';
      tone = 'bad';
   }

   return { at: new Date(epoch * 1000), text, tone };
}

/**
 * The outage itself, as a line IN the timeline.
 *
 * WHY (Ray, 2026-08-11): a box cannot record the moment it dies — it has no
 * power. So the list jumped from "connected 1:07 AM" straight to "connected
 * 10:13 AM" with nothing to say nine hours had vanished, while a separate line
 * above claimed "down for 8 hours 44 minutes". Two descriptions of one outage,
 * with nothing joining them, and it read as a contradiction.
 *
 * The verdict carries `boot` (when it came back) and `dark` (how long), which is
 * exactly enough to place it where it belongs and show both ends.
 */
export function outageEvent(outage: string | undefined): DiagEvent | null {
   if (!outage) return null;
   const boot = Number(/boot=(\d+)/.exec(outage)?.[1]);
   const dark = Number(/dark=(\d+)/.exec(outage)?.[1]);
   if (!Number.isFinite(boot) || !Number.isFinite(dark) || dark <= 0) return null;

   const from = new Date((boot - dark) * 1000);
   const back = new Date(boot * 1000);
   const who = /cause=(.*)$/.exec(outage)?.[1]?.trim() ?? '';
   const hm = (d: Date) =>
      d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }).toUpperCase();

   const tail = who.includes('remote')
      ? 'you restarted it from the dashboard'
      : who.includes('watchdog')
        ? 'it restarted itself and recovered'
        : 'power was cut, someone restarted it, or it crashed';

   // Both ends only when they differ. A 48-second outage rendered "4:33 PM to
   // 4:33 PM" reads as broken; the range earns its place on a long outage,
   // where "overnight" and "during trading" are different problems.
   const span = hm(from) === hm(back) ? '' : ` — ${hm(from)} to ${hm(back)}`;

   return {
      at: from,
      text: `Down for ${humanDuration(dark)}${span} — ${tail}`,
      tone: 'bad',
   };
}

/**
 * One repair the router TRIED, in words that mean something in a shop.
 *
 * net-watchdog.sh writes these to the router's FLASH as each attempt happens —
 * `<epoch> action=<name> result=ok|fail` — so unlike its RAM log they survive
 * the reboot that ends the outage. The router has been sending them on every
 * heartbeat all along; the server used to drop them on the floor.
 *
 * ⚠️ "It tried" and "it worked" are different facts and the useful one is the
 * second. The exit code is recorded precisely so this line can say which, and a
 * run of failed repairs is what explains a box that stayed dark.
 *
 * The names are a closed set, from `_act_name` in net-watchdog.sh. None of them
 * reaches the operator — `restart-resolver` means nothing to someone standing
 * in front of a vendo.
 */
const ACTION_WORDS: Record<string, string> = {
   'restart-agent': 'Restarted the box software to try to reconnect',
   'restart-network': "Restarted the shop's network connection",
   'restart-resolver': 'Restarted the part that looks up website addresses',
   'ifup-wan': "Asked the shop's internet line to reconnect",
   reboot: 'Restarted the whole router',
   other: 'Tried a repair',
};

export function parseAction(line: string): DiagEvent | null {
   const p = String(line).trim().split(/\s+/);
   const epoch = Number(p[0]);
   if (!Number.isFinite(epoch) || epoch <= 0) return null;
   const name = /^action=(.+)$/.exec(p[1] ?? '')?.[1];
   if (!name) return null;
   const ok = /^result=ok$/.test(p[2] ?? '');
   const what = ACTION_WORDS[name] ?? ACTION_WORDS.other;
   return {
      at: new Date(epoch * 1000),
      text: `${what} — ${ok ? 'it worked' : 'it did not help'}`,
      tone: ok ? 'ok' : 'warn',
   };
}

export interface RoadEventRow {
   at: string | Date;
   via: 'direct' | 'relay';
   road: number;
   ip: string;
   claimedIp: string;
}

/**
 * A router changing how it reaches us, as timeline lines.
 *
 * Until this was recorded there was no trace at all: a vendo that took the
 * backup route at 03:12 and came home at 03:41 read `direct` by morning with
 * nothing showing it happened.
 *
 * 🔴 The OLDEST row is a baseline, not an event. The store writes one row the
 * first time it ever sees a device, so a router that has never switched has a
 * history consisting of exactly one `direct` row — and rendering that as "back
 * on the normal route home" invents an outage that never happened.
 */
export function roadEvents(rows: RoadEventRow[] | undefined): DiagEvent[] {
   if (!Array.isArray(rows) || !rows.length) return [];
   const sorted = [...rows].sort(
      (a, b) => new Date(b.at).getTime() - new Date(a.at).getTime()
   );
   // Drop the baseline: the oldest row when it says "direct", because there was
   // nothing to come home from.
   const oldest = sorted[sorted.length - 1];
   const usable =
      oldest.via === 'direct' ? sorted.slice(0, sorted.length - 1) : sorted;

   return usable.map((r) => ({
      at: new Date(r.at),
      text:
         r.via === 'relay'
            ? "Moved to the backup route home — its internet provider had stopped passing traffic to SmartFi"
            : 'Back on the normal route home',
      // A router on the backup road is REACHABLE, so this is never 'bad'. It is
      // working around a fault at the shop's ISP, and that is worth flagging,
      // not alarming about.
      tone: r.via === 'relay' ? 'warn' : 'ok',
   }));
}

export function parseEvents(
   events: string[] | undefined,
   outage?: string,
   /** The repair record — kept by the server since step 12. */
   actions?: string[],
   /** Road switches. Admin path only; the operator API does not serve them. */
   roads?: RoadEventRow[]
): DiagEvent[] {
   const list = Array.isArray(events)
      ? events.map(parseEvent).filter((e): e is DiagEvent => e !== null)
      : [];

   if (Array.isArray(actions)) {
      for (const a of actions) {
         const e = parseAction(a);
         if (e) list.push(e);
      }
   }
   list.push(...roadEvents(roads));

   const gap = outageEvent(outage);
   // Only splice it in if it belongs to the stretch we are showing. An outage
   // older than everything on screen would otherwise appear at the bottom
   // pretending to be the oldest thing that happened.
   if (gap && (!list.length || gap.at.getTime() >= list[0].at.getTime())) {
      list.push(gap);
   }

   // Newest first — the last thing that happened is what matters.
   return list.sort((a, b) => b.at.getTime() - a.at.getTime());
}

/**
 * What we actually know about the tunnel — three states, never two.
 *
 * 🔴 Measured on the live fleet 2026-08-23: 79 vendos were heartbeating that
 * minute, NodeMCU fresh, router seen 0m ago, and every one rendered a red
 * "Not reachable" card. The card asked `(hsAge ?? -1) < 0` and read the answer
 * as a dead tunnel.
 *
 * But `-1` is what the SERVER writes when the router did not report a handshake
 * age at all — `normalizeDiag()` defaults it, and clamps anything unparseable to
 * the same -1. So -1 means "the box did not tell us", and a missing field means
 * the same thing. Neither is evidence of a fault.
 *
 * ⚠️ Unknown must NOT be optimistically read as "up" either. A hybrid router
 * checks in over the open internet, not through the tunnel, so a fresh heartbeat
 * is no proof the tunnel is alive. We do not know. Say so.
 */
export type TunnelState = 'up' | 'down' | 'unknown';

export function tunnelState(hsAge: number | undefined | null): TunnelState {
   if (hsAge === undefined || hsAge === null) return 'unknown';
   if (!Number.isFinite(hsAge)) return 'unknown';
   // Negative is the server's "not reported", never a measured age.
   if (hsAge < 0) return 'unknown';
   return hsAge <= TUNNEL_STALE_SEC ? 'up' : 'down';
}

/**
 * The "SmartFi connection" card: what colour, and what words.
 *
 * Freshness still wins over everything — a box that went quiet four hours ago
 * carries a report that was true when it was sent and is a lie now.
 */
export function tunnelCard(
   hsAge: number | undefined | null,
   stale: boolean
): { tone: 'ok' | 'bad' | 'unknown'; text: string } {
   if (stale) return { tone: 'bad', text: 'Not reachable' };
   const state = tunnelState(hsAge);
   return {
      tone: state === 'up' ? 'ok' : state === 'down' ? 'bad' : 'unknown',
      text: tunnelLine(hsAge ?? undefined),
   };
}

/**
 * "This vendo cannot reach SmartFi, and that is on our side — not the
 * customer's line." That sentence is an accusation against ourselves and it
 * sends someone to look at our infrastructure. It may only be said when the
 * tunnel is KNOWN dead on a report we know is current. An unreported handshake
 * age is not knowledge.
 */
export function blameUs(
   hsAge: number | undefined | null,
   inet: string | undefined,
   stale: boolean
): boolean {
   return !stale && tunnelState(hsAge) === 'down' && inet === 'up';
}
